import { useEffect, useState } from "react";
import { Avatar } from "../components/ui/Avatar";
import { AppShell } from "../components/layout/AppShell";
import { Card } from "../components/ui/Primitives";
import { Badge } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { Input, Select, SearchInput } from "../components/ui/Fields";
import { Modal, ConfirmDialog } from "../components/ui/Modal";
import { useToast } from "../components/ui/Toast";
import { apiRequest, ApiError } from "../api/client";

/**
 * Round 30 (revised per testing feedback) — Unique Public ID system.
 *
 * There is no separate "unique_public_id"/"display_public_id" column
 * anymore. profiles.user_id IS the single canonical public id — the exact
 * same field every screen in the app already shows. Assigning a custom ID
 * here means the server literally overwrites profiles.user_id with it, so
 * it shows up everywhere at once with zero other code needing to know
 * which of two fields is "real", because there's only ever the one.
 *
 * profiles.original_user_id (migration 0061) is a write-once backup of
 * whatever user_id held at signup — this page never shows it as an
 * editable value, only as the "falls back to" number on Remove, and the
 * server is what actually restores it.
 *
 * Every assign/remove call lands on POST/DELETE /admin/unique-ids, which
 * is super_admin-gated and audit-logged server-side (routes/admin/
 * unique-ids.js) — this page has no privileged logic of its own, it's a
 * thin UI over that route, same as every other admin page in this app.
 */

const ID_LEVELS = [1, 2, 3, 4, 5, 6, 7];

interface UniqueIdRow {
  id: string; // internal UUID — used only to key rows and target the assign/remove calls
  user_id: number; // the current, canonical public id
  original_user_id: number; // pre-assignment id, restored on Remove
  username: string;
  avatar_url: string | null;
  id_level: number | null;
}

export function UniqueIdsPage() {
  const [rows, setRows] = useState<UniqueIdRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { showToast } = useToast();

  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<UniqueIdRow | null>(null);
  // Round 48 item 6 — edit an existing assignment (ID and/or ID level) + search the list.
  const [editTarget, setEditTarget] = useState<UniqueIdRow | null>(null);
  const [search, setSearch] = useState("");
  const [isRemoving, setIsRemoving] = useState(false);

  async function load() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await apiRequest<UniqueIdRow[]>("/admin/unique-ids");
      setRows(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not load unique ID assignments");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleRemove() {
    if (!removeTarget) return;
    setIsRemoving(true);
    try {
      await apiRequest(`/admin/unique-ids/${removeTarget.id}`, { method: "DELETE" });
      showToast(`Unique ID removed — falls back to ${removeTarget.original_user_id}`, "success");
      setRemoveTarget(null);
      load();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not remove", "error");
    } finally {
      setIsRemoving(false);
    }
  }

  const term = search.trim().toLowerCase();
  const visibleRows = term
    ? rows.filter(
        (r) =>
          String(r.user_id).includes(term) ||
          String(r.original_user_id).includes(term) ||
          (r.username ?? "").toLowerCase().includes(term)
      )
    : rows;

  return (
    <AppShell title="Unique ID Management">
      <div className="mb-4 flex items-start justify-between gap-4">
        <p className="max-w-lg text-xs text-ink-faint">
          Assigning a custom ID here replaces the user's public ID directly, everywhere it's shown. Their original ID
          is kept safely in the background — removing an assignment restores it automatically.
        </p>
        <Button onClick={() => setIsAssignOpen(true)}>Assign Unique ID</Button>
      </div>

      <div className="mb-4 max-w-sm">
        <SearchInput value={search} onChange={setSearch} placeholder="Search by public ID, original ID or username…" />
      </div>

      {isLoading && <p className="text-sm text-ink-muted">Loading…</p>}
      {error && (
        <div className="flex items-center gap-3">
          <p className="text-sm text-danger">{error}</p>
          <Button size="sm" variant="secondary" onClick={load}>Retry</Button>
        </div>
      )}

      {!isLoading && !error && (
        <Card>
          {visibleRows.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-ink-faint">{rows.length === 0 ? "No unique IDs assigned yet." : "No assignment matches that search."}</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-surface-border text-xs font-medium uppercase tracking-wide text-ink-faint">
                  <th className="px-5 py-3">User</th>
                  <th className="px-5 py-3">Current ID</th>
                  <th className="px-5 py-3">Original ID</th>
                  <th className="px-5 py-3">ID Level</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((row) => (
                  <tr key={row.id} className="border-b border-surface-border last:border-0">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2.5">
                        <Avatar src={row.avatar_url} className="h-7 w-7 rounded-full object-cover" />
                        <span className="font-medium text-ink">{row.username}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <Badge tone="gold">{row.user_id}</Badge>
                    </td>
                    <td className="px-5 py-3 text-ink-muted">{row.original_user_id}</td>
                    <td className="px-5 py-3 text-ink-muted">{row.id_level ?? "—"}</td>
                    <td className="px-5 py-3 text-right">
                      <div className="flex justify-end gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setEditTarget(row)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="danger" onClick={() => setRemoveTarget(row)}>
                          Remove
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      <AssignUniqueIdModal
        isOpen={isAssignOpen}
        onClose={() => setIsAssignOpen(false)}
        onAssigned={() => {
          setIsAssignOpen(false);
          load();
        }}
      />

      <EditUniqueIdModal
        target={editTarget}
        onClose={() => setEditTarget(null)}
        onSaved={() => {
          setEditTarget(null);
          load();
        }}
      />

      <ConfirmDialog
        isOpen={!!removeTarget}
        onClose={() => setRemoveTarget(null)}
        onConfirm={handleRemove}
        isLoading={isRemoving}
        isDangerous
        title="Remove unique ID?"
        confirmLabel="Remove"
        description={
          removeTarget
            ? `${removeTarget.username} will go back to showing their original ID (${removeTarget.original_user_id}) everywhere in the app.`
            : ""
        }
      />
    </AppShell>
  );
}

function AssignUniqueIdModal({
  isOpen,
  onClose,
  onAssigned,
}: {
  isOpen: boolean;
  onClose: () => void;
  onAssigned: () => void;
}) {
  const [searchId, setSearchId] = useState("");
  const [newUserId, setNewUserId] = useState("");
  const [idLevel, setIdLevel] = useState("");
  const [foundProfile, setFoundProfile] = useState<{ id: string; username: string; user_id: number } | null>(null);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (!isOpen) {
      setSearchId("");
      setNewUserId("");
      setIdLevel("");
      setFoundProfile(null);
      setLookupError(null);
    }
  }, [isOpen]);

  async function lookup() {
    if (!searchId) return;
    setIsLookingUp(true);
    setLookupError(null);
    setFoundProfile(null);
    try {
      const row = await apiRequest<{ id: string; username: string; user_id: number }>("/admin/unique-ids/lookup", {
        params: { query: searchId },
      });
      setFoundProfile(row);
    } catch (err) {
      setLookupError(err instanceof ApiError ? err.message : "Lookup failed");
    } finally {
      setIsLookingUp(false);
    }
  }

  async function handleAssign() {
    if (!foundProfile) return;
    setIsSaving(true);
    try {
      await apiRequest("/admin/unique-ids", {
        method: "POST",
        body: {
          id: foundProfile.id,
          new_user_id: Number(newUserId),
          id_level: idLevel ? Number(idLevel) : null,
        },
      });
      showToast("Unique ID assigned", "success");
      onAssigned();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not assign", "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Assign Unique ID"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button
            onClick={handleAssign}
            isLoading={isSaving}
            disabled={!foundProfile || !newUserId}
          >
            Assign
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <div className="flex items-end gap-2">
            <Input
              label="Find User (current or original ID)"
              placeholder="e.g. 74848506"
              value={searchId}
              onChange={(e) => {
                setSearchId(e.target.value);
                setFoundProfile(null);
                setLookupError(null);
              }}
            />
            <Button variant="secondary" onClick={lookup} isLoading={isLookingUp} disabled={!searchId}>
              Find
            </Button>
          </div>
          {foundProfile && (
            <p className="mt-1.5 text-xs text-success">
              Found: {foundProfile.username} (currently {foundProfile.user_id})
            </p>
          )}
          {lookupError && <p className="mt-1.5 text-xs text-danger">{lookupError}</p>}
        </div>

        <Input
          label="New ID"
          placeholder="e.g. 11111"
          value={newUserId}
          onChange={(e) => setNewUserId(e.target.value)}
          disabled={!foundProfile}
        />

        <Select
          label="ID Level (optional)"
          value={idLevel}
          onChange={(e) => setIdLevel(e.target.value)}
          disabled={!foundProfile}
        >
          <option value="">No ID level (shows "ID: n")</option>
          {ID_LEVELS.map((n) => (
            <option key={n} value={n}>{`Level ${n}`}</option>
          ))}
        </Select>
      </div>
    </Modal>
  );
}

/**
 * Round 48 item 6 — edit an existing assignment: change the public ID, the ID level, or
 * both. Leaving "Public ID" unchanged sends no new ID (the server keeps the current one),
 * so an admin can set/clear just the level. The change is saved by the Worker, audit-logged,
 * and pushed live to everyone in a room with that user.
 */
function EditUniqueIdModal({
  target,
  onClose,
  onSaved,
}: {
  target: UniqueIdRow | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [newUserId, setNewUserId] = useState("");
  const [idLevel, setIdLevel] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    if (target) {
      setNewUserId(String(target.user_id));
      setIdLevel(target.id_level ? String(target.id_level) : "");
    }
  }, [target]);

  async function handleSave() {
    if (!target) return;
    setIsSaving(true);
    try {
      await apiRequest("/admin/unique-ids", {
        method: "POST",
        body: {
          id: target.id,
          new_user_id: newUserId && Number(newUserId) !== target.user_id ? Number(newUserId) : undefined,
          id_level: idLevel ? Number(idLevel) : null,
        },
      });
      showToast("Unique ID updated", "success");
      onSaved();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not save", "error");
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={!!target}
      onClose={onClose}
      title={target ? `Edit ID — ${target.username}` : "Edit ID"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleSave} isLoading={isSaving} disabled={!newUserId}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <Input
          label="Public ID"
          value={newUserId}
          onChange={(e) => setNewUserId(e.target.value)}
          hint={target ? `Original ID: ${target.original_user_id}` : undefined}
        />
        <Select label="ID Level" value={idLevel} onChange={(e) => setIdLevel(e.target.value)}>
          <option value="">No ID level (shows "ID: n")</option>
          {ID_LEVELS.map((n) => (
            <option key={n} value={n}>{`Level ${n}`}</option>
          ))}
        </Select>
      </div>
    </Modal>
  );
}
